import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { parse, stringify } from '../../util/urlHelpers';

// Pagination page size might need to be dynamic on responsive page layouts
const RESULT_PAGE_SIZE = 24;

// ----------------- helpers -----------------

const buildSearchQueryString = params => {
  // Remove undefined/null/empty-string keys
  const cleaned = Object.entries(params || {}).reduce((acc, [k, v]) => {
    if (v === undefined || v === null || v === '') return acc;
    return { ...acc, [k]: v };
  }, {});

  // urlHelpers.stringify handles nested objects reasonably for simple use cases
  // If you later need arrays/complex params, we can adjust.
  return stringify(cleaned);
};

const normalizeApiResponse = json => {
  // Expecting:
  // { data: [...], meta: { page, perPage, totalItems, totalPages } }
  const data = Array.isArray(json?.data) ? json.data : [];
  const meta = json?.meta || null;
  return { data, meta };
};

const emptyResponseFor = searchParams => {
  const pageNum = Number(searchParams?.page || 1);
  const perPageNum = Number(searchParams?.perPage || RESULT_PAGE_SIZE);
  return normalizeApiResponse({
    data: [],
    meta: {
      page: Number.isFinite(pageNum) ? pageNum : 1,
      perPage: Number.isFinite(perPageNum) ? perPageNum : RESULT_PAGE_SIZE,
      totalItems: 0,
      totalPages: 0,
    },
  });
};

// ----------------- async thunks -----------------

export const searchListings = createAsyncThunk(
  'SearchPage/searchListings',
  async ({ searchParams }, thunkAPI) => {
    try {
      const qs = buildSearchQueryString(searchParams);
      const res = await fetch(`/api/search?${qs}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });

      // ✅ MVP behavior: if backend isn't ready or returns an error,
      // return empty results instead of rejecting (prevents render crash loops / twitching).
      if (!res.ok) {
        return emptyResponseFor(searchParams);
      }

      const json = await res.json().catch(() => null);
      return normalizeApiResponse(json);
    } catch (e) {
      // ✅ Also fail soft on network errors
      return emptyResponseFor(searchParams);
    }
  }
);

// ----------------- slice -----------------

const searchPageSlice = createSlice({
  name: 'SearchPage',
  initialState: {
    pagination: null, // meta from API
    searchParams: null,
    searchInProgress: false,
    searchListingsError: null,

    // For pure discovery, store results directly (no marketplaceData duck)
    currentPageResults: [], // array of listing objects
    currentPageResultIds: [],

    activeListingId: null,
  },
  reducers: {
    setActiveListing: (state, action) => {
      state.activeListingId = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(searchListings.pending, (state, action) => {
        state.searchParams = action.meta.arg.searchParams;
        state.searchInProgress = true;
        state.searchListingsError = null;
      })
      .addCase(searchListings.fulfilled, (state, action) => {
        const { data, meta } = action.payload || {};
        state.currentPageResults = Array.isArray(data) ? data : [];
        state.currentPageResultIds = (Array.isArray(data) ? data : [])
          .map(l => l?.id)
          .filter(Boolean);

        state.pagination = meta;
        state.searchInProgress = false;

        // ✅ ensure error stays cleared since we soft-fail
        state.searchListingsError = null;
      })
      .addCase(searchListings.rejected, (state, action) => {
        // With soft-fail, this should be rare, but keep it just in case.
        // eslint-disable-next-line no-console
        console.error(action.payload || action.error);
        state.searchInProgress = false;
        state.searchListingsError = action.payload || action.error;
      });
  },
});

// selectors
export const selectSearchState = state => state.SearchPage || {};
export const selectCurrentPageResults = state =>
  (selectSearchState(state).currentPageResults) || [];
export const selectPagination = state => selectSearchState(state).pagination;
export const selectSearchInProgress = state => selectSearchState(state).searchInProgress;
export const selectSearchError = state => selectSearchState(state).searchListingsError;
export const selectActiveListingId = state => selectSearchState(state).activeListingId;

export const { setActiveListing } = searchPageSlice.actions;

export default searchPageSlice.reducer;

// ----------------- loadData -----------------

// ----------------- loadData -----------------

/**
 * MVP loadData:
 * Disable Sharetribe-style route preloading for SearchPage.
 *
 * Why:
 * - Routes.js / routeConfiguration calls loadData automatically.
 * - That causes repeated /api/search calls (404) and re-renders ("twitching").
 * - In MVP, SearchPageWithMap/SearchPageWithGrid fetch via /api/listings in the component.
 */
export const loadData = () => () => Promise.resolve();

